/**
 * Portal Course Group Management API Client
 *
 * Handles operations for course group management with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to course group endpoint.
 */
const courseGroupApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/course-group-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Make an authenticated API request to course group student endpoint.
 */
const courseGroupStudentApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/course-group-student-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Course Group API ====================

/**
 * List all course groups with optional filters.
 *
 * @param {Object} filters - Optional filters { campusId, subject, year, term }
 */
export const listCourseGroups = async (filters = {}) => {
  const result = await courseGroupApiRequest('list', { filters });
  return result.courseGroups || [];
};

/**
 * Get a single course group by ID with computed fields.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const getCourseGroup = async (courseGroupId) => {
  const result = await courseGroupApiRequest('get', { courseGroupId });
  return result.courseGroup;
};

/**
 * Create a new course group.
 *
 * @param {Object} data - Course group data
 */
export const createCourseGroup = async (data) => {
  const result = await courseGroupApiRequest('create', data);
  return result.courseGroup;
};

/**
 * Update a course group.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {Object} updates - Fields to update
 */
export const updateCourseGroup = async (courseGroupId, updates) => {
  const result = await courseGroupApiRequest('update', { courseGroupId, ...updates });
  return result.courseGroup;
};

/**
 * Save the staff-only notes of a course group, on their own so that they cannot overwrite or
 * be overwritten by whatever else is open in the course group form.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string} adminNotes - The full text; empty removes the note
 * @returns {string} the text as saved
 */
export const updateCourseGroupAdminNotes = async (courseGroupId, adminNotes) => {
  const result = await courseGroupApiRequest('updateAdminNotes', { courseGroupId, adminNotes });
  return result.adminNotes || '';
};

/**
 * Delete a course group.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const deleteCourseGroup = async (courseGroupId) => {
  const result = await courseGroupApiRequest('delete', { courseGroupId });
  return result;
};

/**
 * Get unique subjects from MaterialPortal.
 */
export const getSubjects = async () => {
  const result = await courseGroupApiRequest('getSubjects', {});
  return result.subjects || [];
};

/**
 * Get all course group types (1to1, 1to2&3, Group).
 */
export const getCourseGroupTypes = async () => {
  const result = await courseGroupApiRequest('getTypes', {});
  return result.types || [];
};

/**
 * Get unique rooms for a campus from CourseGroupPortal.
 *
 * @param {string} campusId - Optional campus ID to filter rooms
 */
export const getRooms = async (campusId = null) => {
  const result = await courseGroupApiRequest('getRooms', { campusId });
  return result.rooms || [];
};

// ==================== Course Group Student API ====================

/**
 * List students in a course group with computed fields.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const listCourseGroupStudents = async (courseGroupId) => {
  const result = await courseGroupStudentApiRequest('list', { courseGroupId });
  return result.enrollments || [];
};

/**
 * Add a student to a course group.
 *
 * @param {Object} data - { courseGroupId, studentId, credit, startDate, absenceWeeks }
 * @returns {Object} Result with enrollment and optionally capacityWarning, capacityMessage
 */
export const addStudentToCourseGroup = async (data) => {
  const result = await courseGroupStudentApiRequest('add', data);
  return result;
};

/**
 * Update student enrollment info.
 *
 * @param {string} enrollmentId - Enrollment ID
 * @param {Object} updates - Fields to update { credit, absenceWeeks, startDate, endDate }
 */
export const updateCourseGroupStudent = async (enrollmentId, updates) => {
  const result = await courseGroupStudentApiRequest('update', { enrollmentId, ...updates });
  return result.enrollment;
};

/**
 * Remove a student from a course group.
 *
 * @param {string} enrollmentId - Enrollment ID
 */
export const removeStudentFromCourseGroup = async (enrollmentId) => {
  const result = await courseGroupStudentApiRequest('remove', { enrollmentId });
  return result;
};

/**
 * Add or subtract credit from a student enrollment with audit logging.
 * Supports credit transfer between course enrollment and student balance.
 *
 * @param {string} enrollmentId - Enrollment ID
 * @param {number} delta - Amount to add (positive) or subtract (negative)
 * @param {string} description - Required description for the credit adjustment
 * @param {Object} options - Optional parameters
 * @param {boolean} options.useStudentCredit - If true, deduct from student balance when adding credit
 * @param {boolean} options.transferToStudent - If true, add to student balance when subtracting credit
 * @param {string} options.filename - Optional filename for attachment
 * @param {string} options.contentType - Optional MIME type for attachment
 * @returns {Object} Result with enrollment, audit, and optionally uploadUrl/contentType
 */
export const addCreditToStudent = async (enrollmentId, delta, description, options = {}) => {
  const data = {
    enrollmentId,
    delta,
    description,
    useStudentCredit: options.useStudentCredit || false,
    transferToStudent: options.transferToStudent || false
  };

  // Add file info if provided
  if (options.filename && options.contentType) {
    data.filename = options.filename;
    data.contentType = options.contentType;
  }

  const result = await courseGroupStudentApiRequest('addCredit', data);
  return result;
};

/**
 * Upload credit audit attachment to S3 using presigned URL.
 *
 * @param {string} uploadUrl - Presigned S3 URL
 * @param {File} file - File object to upload
 * @param {string} contentType - MIME type of the file
 * @returns {boolean} True if upload succeeded
 */
export const uploadCreditAttachment = async (uploadUrl, file, contentType) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType
    },
    body: file
  });

  if (!response.ok) {
    throw new Error('Failed to upload attachment');
  }

  return true;
};

// ==================== Constants ====================

/**
 * Year options (Kindy, Pre-primary, Year 1-12)
 */
export const YEAR_OPTIONS = [
  { value: -1, label: 'Kindy' },
  { value: 0, label: 'Pre-primary' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `Year ${i + 1}`
  }))
];

/**
 * Term options (1-4, special)
 */
export const TERM_OPTIONS = [
  { value: 1, label: 'Term 1' },
  { value: 2, label: 'Term 2' },
  { value: 3, label: 'Term 3' },
  { value: 4, label: 'Term 4' },
  { value: 0, label: 'Special Term (Holiday)' }
];

/**
 * Day of week options (1=Monday to 7=Sunday)
 */
export const DAY_OF_WEEK_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' }
];

/**
 * Get day of week label
 */
export const getDayOfWeekLabel = (dayOfWeek) => {
  const option = DAY_OF_WEEK_OPTIONS.find(d => d.value === dayOfWeek);
  return option ? option.label : '';
};

/**
 * Get year label
 */
export const getYearLabel = (year) => {
  if (year === -1) return 'Kindy';
  if (year === 0) return 'Pre-primary';
  if (year >= 1 && year <= 12) return `Year ${year}`;
  if (year === 13) return 'Graduated';
  return '';
};

/**
 * Get term label
 */
export const getTermLabel = (term) => {
  if (term === 0 || term === '0') return 'Special Term';
  if (term >= 1 && term <= 4) return `Term ${term}`;
  return '';
};

/**
 * Check if term is special (term = 0)
 */
export const isSpecialTerm = (term) => {
  return term === 0 || term === '0';
};

/**
 * Format schedule for display
 */
export const formatSchedule = (schedule) => {
  const day = getDayOfWeekLabel(schedule.dayOfWeek);
  return `${day} ${schedule.startTime} - ${schedule.endTime}`;
};

/**
 * Format schedules array for display
 */
export const formatSchedules = (schedules) => {
  if (!schedules || schedules.length === 0) return 'No schedule';
  return schedules.map(formatSchedule).join(', ');
};

// ==================== Week Helper Functions ====================

/**
 * Get the Monday (start) of the week containing the given date.
 * @param {Date} date - Any date
 * @returns {Date} Monday of that week
 */
export const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  // Convert Sunday (0) to 7 for easier calculation
  const dayOffset = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - dayOffset);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get the Sunday (end) of the week containing the given date.
 * @param {Date} date - Any date
 * @returns {Date} Sunday of that week
 */
export const getWeekEnd = (date) => {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  return weekEnd;
};

/**
 * Format week range for display (e.g., "6 Jan - 12 Jan 2026")
 * @param {Date} weekStart - Monday of the week
 * @param {Date} weekEnd - Sunday of the week
 * @returns {string} Formatted range
 */
export const formatWeekRange = (weekStart, weekEnd) => {
  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const startMonth = weekStart.toLocaleDateString('en-AU', { month: 'short' });
  const endMonth = weekEnd.toLocaleDateString('en-AU', { month: 'short' });
  const endYear = weekEnd.getFullYear();

  if (startMonth === endMonth) {
    return `${startDay} - ${endDay} ${startMonth} ${endYear}`;
  }
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
};

/**
 * Format a single date for short display (e.g., "6 Jan")
 * @param {Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDateShort = (date) => {
  const day = date.getDate();
  const month = date.toLocaleDateString('en-AU', { month: 'short' });
  return `${day} ${month}`;
};

/**
 * Get the date for a specific dayOfWeek in a given week.
 * @param {Date} weekStart - Monday of the week
 * @param {number} dayOfWeek - 1=Monday, 7=Sunday
 * @returns {Date} The date for that day
 */
export const getDateForDayInWeek = (weekStart, dayOfWeek) => {
  const d = new Date(weekStart);
  d.setDate(weekStart.getDate() + (dayOfWeek - 1));
  return d;
};

/**
 * Format date as YYYY-MM-DD string
 * @param {Date} date - Date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateYMD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if a date string (YYYY-MM-DD) falls within a week.
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {Date} weekStart - Monday of the week
 * @param {Date} weekEnd - Sunday of the week
 * @returns {boolean} True if date is within the week
 */
export const isDateInWeek = (dateStr, weekStart, weekEnd) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date >= weekStart && date <= weekEnd;
};

/**
 * Get day of week from date string (1=Monday, 7=Sunday)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {number} Day of week (1-7)
 */
export const getDayOfWeekFromDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.getDay();
  // Convert Sunday (0) to 7
  return day === 0 ? 7 : day;
};

const courseGroupPortalApi = {
  listCourseGroups,
  getCourseGroup,
  createCourseGroup,
  updateCourseGroup,
  deleteCourseGroup,
  getSubjects,
  getCourseGroupTypes,
  getRooms,
  listCourseGroupStudents,
  addStudentToCourseGroup,
  updateCourseGroupStudent,
  removeStudentFromCourseGroup,
  addCreditToStudent,
  uploadCreditAttachment,
  YEAR_OPTIONS,
  TERM_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  getDayOfWeekLabel,
  getYearLabel,
  getTermLabel,
  isSpecialTerm,
  formatSchedule,
  formatSchedules,
  getWeekStart,
  getWeekEnd,
  formatWeekRange,
  formatDateShort,
  getDateForDayInWeek,
  formatDateYMD,
  isDateInWeek,
  getDayOfWeekFromDate
};

export default courseGroupPortalApi;
